// The update_my_settings line in a chat turn: what changed, where it applies
// and Undo. Rendering it never applies anything (a chat loaded from history
// must not flip the theme); App applies a live result through the transport's
// tool-output hook. Undo is offered only to the account the change was made
// for (a reader of a shared chat sees the line, never an Undo that would write
// their own settings), and only while the change is still what that person
// has, so an old chat never undoes a newer choice.
//
// An app-language switch is never applied by the tool (operator decision
// 2026-09-25): the result proposes it, and its own line asks "Switch the app
// to Svenska?" with Apply and Dismiss. Apply is the person's click, which PUTs
// the proposal from the browser and adopts the answer, as Undo does; Dismiss
// writes nothing. Both are offered only to the account the proposal was made
// for, and only while it does not already have that language.
import { useState } from 'react';
import { useSettingsUndo } from './use-settings-undo';
import { WorkspaceIcon } from './icons';
import {
  SETTINGS_TOOL,
  hasProposal,
  isOwnChange,
  settingsChangeFromTool,
  useAccountSettings,
  type ProposalView,
  type SettingsChange,
  type SettingsProposal,
} from '@/lib/account-settings';
import { useSettingsChangeCopy } from './settings-change-copy';
import { useNamespaceCopy } from '@/i18n/ns';
import settingsActions from '@/i18n/ns/settings-actions';

type ApplyState = 'idle' | 'applying' | 'applied' | 'dismissed' | 'failed';

function ChangeLine({
  change,
  toolCallId,
  compact = false,
}: {
  change: SettingsChange;
  toolCallId?: string;
  compact?: boolean;
}) {
  const copy = useSettingsChangeCopy();
  const undoCopy = useNamespaceCopy(settingsActions);
  const { state, undoable, undo } = useSettingsUndo(change, toolCallId);
  return (
    <div className="ws-setting-change" role="status">
      <WorkspaceIcon name="check" size={13} />
      <span title={compact ? copy.change(change) : undefined}>{copy.change(change)}</span>
      {undoable && (
        <button type="button" disabled={state === 'undoing'} onClick={() => void undo()}>
          {undoCopy.undo}
        </button>
      )}
      {state === 'undone' && <span className="ws-setting-change-note">{undoCopy.undone}</span>}
      {state === 'failed' && (
        <span className="ws-setting-change-note" role="alert">
          {copy.error}
        </span>
      )}
    </div>
  );
}

/**
 * What the proposal's line shows: someone else's proposal is only a record;
 * the owner is asked until they apply or dismiss it, or already have that
 * language (it was applied since, here or on another device).
 */
function proposalView(own: boolean, state: ApplyState, has: boolean): ProposalView {
  if (!own) return 'offered';
  if (state === 'dismissed') return 'dismissed';
  return state === 'applied' || has ? 'applied' : 'ask';
}

function ProposalLine({
  change,
  proposal,
}: {
  change: SettingsChange;
  proposal: SettingsProposal;
}) {
  const copy = useSettingsChangeCopy();
  const control = useAccountSettings();
  const [state, setState] = useState<ApplyState>('idle');
  const own = control !== null && isOwnChange(change, control.userId);
  const view = proposalView(own, state, own && hasProposal(proposal, control.settings));
  const apply = async () => {
    if (!control) return;
    setState('applying');
    try {
      await control.applyProposal(proposal);
      setState('applied');
    } catch {
      setState('failed');
    }
  };
  return (
    <div className="ws-setting-change" role="status">
      <WorkspaceIcon name={view === 'ask' ? 'globe' : 'check'} size={13} />
      <span>{copy.proposal(proposal, view)}</span>
      {view === 'ask' && (
        <>
          <button type="button" disabled={state === 'applying'} onClick={() => void apply()}>
            {copy.approve}
          </button>
          <button
            type="button"
            disabled={state === 'applying'}
            onClick={() => setState('dismissed')}
          >
            {copy.reject}
          </button>
        </>
      )}
      {view === 'ask' && state === 'failed' && (
        <span className="ws-setting-change-note" role="alert">
          {copy.error}
        </span>
      )}
    </div>
  );
}

export function SettingsChangeCard({
  output,
  toolCallId,
  compact = false,
}: {
  output: unknown;
  toolCallId?: string;
  compact?: boolean;
}) {
  const change = settingsChangeFromTool(SETTINGS_TOOL, output);
  if (!change) return null;
  return (
    <>
      {change.changed.length > 0 && (
        <ChangeLine change={change} toolCallId={toolCallId} compact={compact} />
      )}
      {change.proposed && <ProposalLine change={change} proposal={change.proposed} />}
    </>
  );
}
